import styled from 'styled-components';

import { media } from 'src/styles/breakpoints';

export const UsersTabs = styled.nav`
  display: flex;
  flex-wrap: nowrap;
  align-items: stretch;
  gap: 1.5rem;
  flex: 1 1 auto;
  min-width: 0;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

export const UsersTab = styled.button<{ $active: boolean }>`
  flex-shrink: 0;
  position: relative;
  display: inline-flex;
  align-items: center;
  padding: 0.75rem 0;
  border: none;
  background: transparent;
  color: ${({ theme, $active }) => ($active ? theme.primary : theme.midGrey)};
  font-size: 1rem;
  font-weight: ${({ $active }) => ($active ? 700 : 500)};
  font-family: inherit;
  cursor: pointer;
  white-space: nowrap;
  transition: color 0.15s ease;

  &::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: -1px;
    height: 2px;
    border-radius: 2px 2px 0 0;
    background: ${({ theme, $active }) => ($active ? theme.primary : 'transparent')};
  }

  &:hover {
    color: ${({ theme }) => theme.primary};
  }
`;

export const TabsRow = styled.div`
  display: flex;
  align-items: stretch;
  justify-content: space-between;
  gap: 1rem;
  margin: 0 0 1.5rem;
  border-bottom: 1px solid ${({ theme }) => theme.grey};
  flex-wrap: wrap;
`;

export const TabActions = styled.div`
  display: inline-flex;
  align-items: center;
  align-self: center;
  gap: 0.5rem;
  flex-shrink: 0;
`;

export const Breadcrumb = styled.nav`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.35rem;
  margin-bottom: 1rem;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.midGrey};
`;

export const BreadcrumbLink = styled.button`
  border: none;
  background: transparent;
  padding: 0;
  color: ${({ theme }) => theme.primary};
  font: inherit;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

export const BreadcrumbSep = styled.span`
  color: ${({ theme }) => theme.midGrey};
`;

export const DetailHeader = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.25rem;
`;

export const DetailTitleRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem;
`;

export const DetailTitle = styled.h1`
  margin: 0;
  font-size: 2rem;
  font-weight: 700;
  color: ${({ theme }) => theme.dark};
  line-height: 1;
`;

export const DetailBadges = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.65rem;
`;

export const DetailMeta = styled.p`
  margin: 0.5rem 0 0;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.midGrey};
`;

export const BalanceBlock = styled.div`
  text-align: right;

  ${media.md} {
    text-align: left;
  }
`;

export const BalanceLabel = styled.span`
  font-size: 0.875rem;
  font-weight: 500;
  color: ${({ theme }) => theme.midGrey};
`;

export const BalanceValue = styled.span`
  font-size: 2rem;
  font-weight: 400;
  color: ${({ theme }) => theme.dark};
  margin-left: 0.35rem;
  line-height: 1;
`;

export const WalletsMeta = styled.p`
  margin: 0 0 1rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: ${({ theme }) => theme.midGrey};
`;

export const BalanceHint = styled.div`
  margin-top: 0.25rem;
  font-size: 0.75rem;
  color: ${({ theme }) => theme.midGrey};
`;

export const NameLink = styled.button<{ $muted?: boolean }>`
  border: none;
  background: transparent;
  padding: 0;
  color: ${({ theme, $muted }) => ($muted ? theme.midGrey : theme.primary)};
  font: inherit;
  font-weight: ${({ $muted }) => ($muted ? 400 : 600)};
  cursor: pointer;
  text-align: left;

  &:hover {
    text-decoration: ${({ $muted }) => ($muted ? 'none' : 'underline')};
  }
`;

export const ActionIconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  padding: 0;
  border: 1px solid ${({ theme }) => theme.grey};
  border-radius: 0.5rem;
  background: ${({ theme }) => theme.background};
  color: ${({ theme }) => theme.midGrey};
  cursor: pointer;
  transition:
    color 0.15s ease,
    border-color 0.15s ease,
    background 0.15s ease;

  &:hover:not(:disabled) {
    color: ${({ theme }) => theme.dark};
    border-color: ${({ theme }) => theme.midGrey};
    background: ${({ theme }) => theme.lightGrey};
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

export const ActionsRow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
`;

export const SectionHint = styled.p`
  margin: 0 0 1rem;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.midGrey};
`;

export const TabPanel = styled.div`
  width: 100%;
  min-width: 0;
`;

export const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem 1.25rem;

  ${media.md} {
    grid-template-columns: 1fr;
  }
`;

export const FieldCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  min-width: 0;
  padding: 0.85rem 1rem;
  border: 1px solid ${({ theme }) => theme.grey};
  border-radius: 0.75rem;
  background: ${({ theme }) => theme.background};
`;

export const FieldLabel = styled.div`
  font-size: 0.8125rem;
  font-weight: 500;
  color: ${({ theme }) => theme.midGrey};
  margin-bottom: 0.35rem;
`;

export const FieldValue = styled.div`
  font-size: 1rem;
  font-weight: 500;
  color: ${({ theme }) => theme.dark};
  word-break: break-word;
`;

export const FieldSelect = styled.select`
  width: 100%;
  margin-top: 0.15rem;
  border: none;
  background: transparent;
  padding: 0;
  font: inherit;
  font-size: 1rem;
  font-weight: 500;
  color: ${({ theme }) => theme.dark};
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23666' d='M6 9L1 4h10z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0 center;

  &:focus {
    outline: none;
  }

  &:disabled {
    cursor: default;
    opacity: 0.7;
    background-image: none;
  }
`;

export const FieldValueRow = styled.div`
  min-width: 0;
`;

export const CopyIconButton = styled.button`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.75rem;
  padding: 0;
  border: none;
  border-radius: 0.375rem;
  background: transparent;
  color: ${({ theme }) => theme.midGrey};
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.dark};
    background: ${({ theme }) => theme.lightGrey};
  }
`;

export const SectionToolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem 1rem;
  margin-bottom: 1rem;
`;

export const SectionTitle = styled.h2`
  margin: 0;
  font-size: 1.125rem;
  font-weight: 700;
  color: ${({ theme }) => theme.dark};
`;

export const SectionControls = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem;
  margin-left: auto;
`;

export const CompactSelect = styled.select`
  height: 2.5rem;
  min-width: 8.5rem;
  border-radius: 0.5rem;
  border: 1px solid ${({ theme }) => theme.grey};
  padding: 0 2.25rem 0 0.85rem;
  outline: none;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.dark};
  background-color: ${({ theme }) => theme.background};
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23666' d='M6 9L1 4h10z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.75rem center;
  cursor: pointer;

  &:hover {
    border-color: ${({ theme }) => theme.midGrey};
  }

  &:focus {
    border-color: ${({ theme }) => theme.primary};
  }
`;

export const SearchControl = styled.div`
  width: 14rem;
  max-width: 100%;
`;

export const OverviewGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem 1.5rem;

  ${media.md} {
    grid-template-columns: 1fr;
  }
`;

export const OverviewCard = styled.section`
  border: 1px solid ${({ theme }) => theme.grey};
  border-radius: 0.875rem;
  padding: 1.25rem;
  background: ${({ theme }) => theme.background};
`;

export const OverviewCardTitle = styled.h3`
  margin: 0 0 1rem;
  font-size: 0.9375rem;
  font-weight: 600;
  color: ${({ theme }) => theme.dark};
`;

export const OverviewItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 0;
`;

export const OverviewLabel = styled.span`
  font-size: 0.75rem;
  font-weight: 500;
  color: ${({ theme }) => theme.midGrey};
`;

export const OverviewValue = styled.div`
  font-size: 0.875rem;
  font-weight: 500;
  color: ${({ theme }) => theme.dark};
  word-break: break-word;
`;

export const MaskedEmail = styled.span`
  font-variant-numeric: tabular-nums;
`;

export const HoldingsList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

export const HoldingRow = styled.li`
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.dark};
`;

export const ChartWrap = styled.div`
  border: 1px solid ${({ theme }) => theme.grey};
  border-radius: 0.875rem;
  padding: 1rem 1.25rem 1rem;
  margin-bottom: 1.25rem;
  background: ${({ theme }) => theme.background};
  width: 100%;
  box-sizing: border-box;
  overflow: hidden;
`;

export const ChartHeader = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.35rem;
`;

export const ChartStat = styled.div`
  font-size: 2rem;
  font-weight: 400;
  letter-spacing: -0.075rem;
  color: ${({ theme }) => theme.dark};
  line-height: 1.2;
`;

export const ChartStatSub = styled.div`
  margin-top: 0.25rem;
  margin-bottom: 1rem;
  font-size: 1rem;
  font-weight: 400;
  color: ${({ theme }) => theme.dark};
`;

export const ChartCanvas = styled.div`
  width: calc(100% + 2.5rem);
  margin-left: -1.25rem;
  margin-right: -1.25rem;
`;

export const ChartSvg = styled.svg`
  width: 100%;
  height: auto;
  aspect-ratio: 960 / 240;
  display: block;
`;

export const ChartLegend = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin-top: 0.75rem;
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.midGrey};
`;

export const ChartLegendDot = styled.span`
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 50%;
  background: ${({ theme }) => theme.primary};
  flex-shrink: 0;
`;

export const ChartBars = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 0.35rem;
  height: 7rem;
  margin-top: 0.75rem;
`;

export const ChartBar = styled.div<{ $height: number }>`
  flex: 1;
  min-width: 0;
  height: ${({ $height }) => `${Math.max($height, 2)}%`};
  border-radius: 0.25rem 0.25rem 0 0;
  background: ${({ theme }) => theme.primary};
  opacity: 0.85;
`;

export const ChartLabels = styled.div`
  display: flex;
  gap: 0.35rem;
  margin-top: 0.35rem;
`;

export const ChartLabel = styled.span`
  flex: 1;
  min-width: 0;
  text-align: center;
  font-size: 0.625rem;
  color: ${({ theme }) => theme.midGrey};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const EmptyState = styled.div`
  padding: 2rem 1rem;
  text-align: center;
  color: ${({ theme }) => theme.midGrey};
  font-size: 0.875rem;
`;

export const KycDocLink = styled.a`
  color: ${({ theme }) => theme.primary};
  font-weight: 500;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

export const ToolbarRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 1rem;
`;

export const KycMediaGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem 1.25rem;
  margin-top: 1rem;

  ${media.md} {
    grid-template-columns: 1fr;
  }
`;

export const KycMediaBlock = styled.div`
  border: 1px solid ${({ theme }) => theme.grey};
  border-radius: 0.75rem;
  padding: 0.85rem 1rem;
  background: ${({ theme }) => theme.background};
`;

export const DocThumbs = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
  margin-top: 0.75rem;
`;

export const DocThumb = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
`;

export const DocImage = styled.img`
  width: 100%;
  aspect-ratio: 4 / 3;
  object-fit: cover;
  border-radius: 0.5rem;
  border: 1px solid ${({ theme }) => theme.grey};
  background: ${({ theme }) => theme.lightGrey};
`;

export const DocPlaceholder = styled.div`
  width: 100%;
  aspect-ratio: 4 / 3;
  border-radius: 0.5rem;
  border: 1px dashed ${({ theme }) => theme.grey};
  background: ${({ theme }) => theme.lightGrey};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.midGrey};
  font-size: 0.75rem;
`;

export const DocCaption = styled.span`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.midGrey};
`;

export const PhotoImage = styled.img`
  width: 100%;
  max-height: 16rem;
  object-fit: cover;
  border-radius: 0.5rem;
  border: 1px solid ${({ theme }) => theme.grey};
  background: ${({ theme }) => theme.lightGrey};
  margin-top: 0.75rem;
`;

export const FeedbackArea = styled.textarea`
  width: 100%;
  min-height: 5.5rem;
  margin-top: 1rem;
  box-sizing: border-box;
  border-radius: 0.75rem;
  border: 1px solid ${({ theme }) => theme.grey};
  padding: 0.85rem 1rem;
  font: inherit;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.dark};
  background: ${({ theme }) => theme.background};
  resize: vertical;

  &::placeholder {
    color: ${({ theme }) => theme.midGrey};
  }

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.primary};
  }
`;

export const FeedbackLabel = styled.div`
  margin-top: 1.25rem;
  font-size: 0.75rem;
  font-weight: 500;
  color: ${({ theme }) => theme.midGrey};
`;

export const CenterActions = styled.div`
  display: flex;
  justify-content: center;
  gap: 0.75rem;
  margin-top: 1.5rem;
`;

export const PortfolioLayout = styled.div`
  display: grid;
  grid-template-columns: minmax(12rem, 0.9fr) minmax(0, 1.6fr);
  gap: 1.25rem;
  margin-top: 1rem;

  ${media.md} {
    grid-template-columns: 1fr;
  }
`;

export const DonutWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
`;

export const DonutChart = styled.div<{ $stops: string }>`
  width: 9.5rem;
  height: 9.5rem;
  border-radius: 50%;
  background: conic-gradient(${({ $stops }) => $stops});
  position: relative;

  &::after {
    content: '';
    position: absolute;
    inset: 1.75rem;
    border-radius: 50%;
    background: ${({ theme }) => theme.background};
  }
`;

export const LegendList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`;

export const LegendItem = styled.li`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.dark};
`;

export const LegendDot = styled.span<{ $color: string }>`
  width: 0.55rem;
  height: 0.55rem;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  flex-shrink: 0;
`;

export const ProgressTrack = styled.div`
  height: 0.4rem;
  border-radius: 999px;
  background: ${({ theme }) => theme.lightGrey};
  overflow: hidden;
  margin-top: 0.5rem;
`;

export const ProgressFill = styled.div<{ $pct: number }>`
  height: 100%;
  width: ${({ $pct }) => `${Math.min(Math.max($pct, 0), 100)}%`};
  background: ${({ theme }) => theme.primary};
  border-radius: inherit;
`;

export const StatusText = styled.span<{ $tone?: 'positive' | 'negative' | 'muted' | 'warning' }>`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${({ theme, $tone }) => {
    if ($tone === 'positive') return theme.positive;
    if ($tone === 'negative') return theme.negative;
    if ($tone === 'warning') return theme.middle;
    return theme.midGrey;
  }};
`;
